// src/pages/DashboardPage.tsx
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import api from '../services/api'
import { useAuthStore } from '../store/authStore'
import { 
  DollarSign, 
  ShoppingCart, 
  Users, 
  Package,
  TrendingUp,
  AlertTriangle,
  Plus,
  FileText,
  Shield,
  User
} from 'lucide-react'

interface ResumenDiario {
  fecha: string
  cantidad_ventas: number
  total_ventas: number
  total_subtotal0: number
  total_subtotal12: number
  total_iva: number
}

interface AlertaStock {
  id_producto: number
  detalle: string
  stock_actual: number
  nivel_alerta: string
}

interface MisVentas {
  cantidad: number
  total: number
}

// Dashboard para ADMINISTRADOR
function DashboardAdmin({ resumen, alertas }: { resumen: ResumenDiario | null, alertas: AlertaStock[] }) {
  const stats = [
    {
      label: 'Ventas del día',
      value: resumen?.cantidad_ventas || 0,
      icon: ShoppingCart,
      color: 'bg-blue-500'
    },
    {
      label: 'Total vendido',
      value: `$${(resumen?.total_ventas || 0).toFixed(2)}`,
      icon: DollarSign,
      color: 'bg-green-500'
    },
    {
      label: 'IVA recaudado',
      value: `$${(resumen?.total_iva || 0).toFixed(2)}`,
      icon: TrendingUp,
      color: 'bg-purple-500'
    },
    {
      label: 'Alertas stock',
      value: alertas.length,
      icon: AlertTriangle,
      color: alertas.length > 0 ? 'bg-red-500' : 'bg-gray-400'
    }
  ]

  return (
    <>
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon
          return (
            <div key={index} className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center">
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-500">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link to="/notas-venta" className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
          <ShoppingCart className="w-10 h-10 text-primary-600 mb-3" />
          <h3 className="font-semibold text-gray-800">Notas de Venta</h3>
          <p className="text-sm text-gray-500">Ver historial de ventas</p>
        </Link>
        <Link to="/usuarios" className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
          <Users className="w-10 h-10 text-green-600 mb-3" />
          <h3 className="font-semibold text-gray-800">Usuarios</h3>
          <p className="text-sm text-gray-500">Gestionar usuarios del sistema</p>
        </Link>
        <Link to="/productos" className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
          <Package className="w-10 h-10 text-purple-600 mb-3" />
          <h3 className="font-semibold text-gray-800">Productos</h3>
          <p className="text-sm text-gray-500">Ver catálogo de productos</p>
        </Link>
      </div>

      {/* Alertas de Stock */}
      {alertas.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
            <h2 className="font-semibold text-gray-800">Alertas de Stock</h2>
          </div>
          <div className="divide-y">
            {alertas.slice(0, 5).map((alerta) => (
              <div key={alerta.id_producto} className="px-6 py-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-800">{alerta.detalle}</p>
                  <p className="text-sm text-gray-500">Stock: {alerta.stock_actual} unidades</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  alerta.nivel_alerta === 'SIN_STOCK' 
                    ? 'bg-red-100 text-red-700'
                    : alerta.nivel_alerta === 'CRITICO'
                    ? 'bg-orange-100 text-orange-700'
                    : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {alerta.nivel_alerta}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  )
}

// Dashboard para DISTRIBUIDOR - Solo sus ventas
function DashboardDistribuidor({ misVentas }: { misVentas: MisVentas }) {
  const stats = [
    {
      label: 'Mis ventas hoy',
      value: misVentas.cantidad,
      icon: ShoppingCart,
      color: 'bg-blue-500'
    },
    {
      label: 'Mi total vendido',
      value: `$${misVentas.total.toFixed(2)}`,
      icon: DollarSign,
      color: 'bg-green-500'
    }
  ]

  return (
    <>
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon
          return (
            <div key={index} className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center">
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-500">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link to="/notas-venta/nueva" className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow border-2 border-primary-200">
          <Plus className="w-10 h-10 text-primary-600 mb-3" />
          <h3 className="font-semibold text-gray-800">Nueva Venta</h3>
          <p className="text-sm text-gray-500">Crear una nota de venta</p>
        </Link>
        <Link to="/notas-venta" className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
          <FileText className="w-10 h-10 text-blue-600 mb-3" />
          <h3 className="font-semibold text-gray-800">Mis Ventas</h3>
          <p className="text-sm text-gray-500">Ver mi historial de ventas</p>
        </Link>
        <Link to="/mi-inventario" className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
          <Package className="w-10 h-10 text-purple-600 mb-3" />
          <h3 className="font-semibold text-gray-800">Mi Inventario</h3>
          <p className="text-sm text-gray-500">Ver productos asignados</p>
        </Link>
      </div>
    </>
  )
}

// Dashboard para CLIENTE - Solo información personal
function DashboardCliente({ user }: { user: any }) {
  return (
    <>
      {/* Info del cliente */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 bg-primary-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
            {user?.nombre?.charAt(0).toUpperCase()}
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">{user?.nombre}</h2>
            <p className="text-gray-500">{user?.email}</p>
            <span className="inline-block mt-1 px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
              Cliente
            </span>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Link to="/productos" className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
          <Package className="w-10 h-10 text-purple-600 mb-3" />
          <h3 className="font-semibold text-gray-800">Catálogo de Productos</h3>
          <p className="text-sm text-gray-500">Ver todos los productos disponibles</p>
        </Link>
        <Link to="/verificar" className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
          <Shield className="w-10 h-10 text-blue-600 mb-3" />
          <h3 className="font-semibold text-gray-800">Verificar Documento</h3>
          <p className="text-sm text-gray-500">Validar autenticidad de notas de venta</p>
        </Link>
      </div>

      {/* Mensaje informativo */}
      <div className="bg-blue-50 rounded-xl p-6 text-center">
        <User className="w-12 h-12 text-blue-500 mx-auto mb-3" />
        <h3 className="font-semibold text-gray-800 mb-2">¡Bienvenido a BIO'DACAR!</h3>
        <p className="text-gray-600">
          Como cliente puedes ver nuestro catálogo de productos y verificar la autenticidad de tus documentos de compra.
        </p>
      </div>
    </>
  )
}

export function DashboardPage() {
  const user = useAuthStore(state => state.user)
  const accessToken = useAuthStore(state => state.accessToken)
  const isHydrated = useAuthStore(state => state.isHydrated)
  const [resumen, setResumen] = useState<ResumenDiario | null>(null)
  const [alertas, setAlertas] = useState<AlertaStock[]>([])
  const [misVentas, setMisVentas] = useState<MisVentas>({ cantidad: 0, total: 0 })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Esperar a que Zustand termine de rehidratar desde localStorage
    if (!isHydrated) {
      return
    }

    // Esperar a que el token esté disponible
    if (!accessToken || !user) {
      return
    }

    const fetchData = async () => {
      try {
        if (user?.rol === 'ADMINISTRADOR') {
          const [resumenRes, alertasRes] = await Promise.all([
            api.get('/notas-venta/resumen/diario'),
            api.get('/inventario/alertas')
          ])
          setResumen(resumenRes.data)
          setAlertas(alertasRes.data.alertas || [])
        } else if (user?.rol === 'DISTRIBUIDOR') {
          // Solo obtener mis ventas
          const res = await api.get('/notas-venta/resumen/diario')
          setMisVentas({
            cantidad: res.data.cantidad_ventas || 0,
            total: res.data.total_ventas || 0
          })
        }
        // Para CLIENTE no necesitamos cargar datos adicionales
      } catch (error) {
        console.error('Error cargando dashboard:', error)
      } finally {
        setLoading(false)
      }
    }

    // Si es cliente, no necesita fetch
    if (user?.rol === 'CLIENTE') {
      setLoading(false)
      return
    }

    fetchData()
  }, [user?.rol, accessToken, user, isHydrated])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
          <p className="text-gray-500">Bienvenido, {user?.nombre}</p>
        </div>
        {user?.rol !== 'CLIENTE' && (
          <Link
            to="/notas-venta/nueva"
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nueva Nota de Venta
          </Link>
        )}
      </div>

      {user?.rol === 'ADMINISTRADOR' && (
        <DashboardAdmin resumen={resumen} alertas={alertas} />
      )}
      
      {user?.rol === 'DISTRIBUIDOR' && (
        <DashboardDistribuidor misVentas={misVentas} />
      )}
      
      {user?.rol === 'CLIENTE' && (
        <DashboardCliente user={user} />
      )}
    </div>
  )
}
